'use client'

import { useMemo } from 'react'
import { Pagination } from '@/General/Pagination'
import {
  useQuickSearch,
  QuickSearchSortOption,
} from '@/QuickSearch/useQuickSearch'
import { useSearchParams } from 'next/navigation'
import { BlogSearchResultsList } from '../SearchResultsList'
import { ITEMS_PER_PAGE, sortOptions } from '../constants'

export const BlogSearchResultsClient = ({
  query,
  sort,
  currentPage,
}: BlogSearchResultsClientProps) => {
  const searchParams = useSearchParams()

  // Memoize pagination to prevent unnecessary re-renders
  const pagination = useMemo(
    () => ({
      page: currentPage,
      limit: ITEMS_PER_PAGE,
    }),
    [currentPage]
  )

  // Map sort string to QuickSearchSortOption
  // Default to RELEVANCE
  const sortOption = useMemo((): QuickSearchSortOption => {
    if (!sort) return 'RELEVANCE'
    // If it's a sort string from constants, map it
    // DATE_ADDED_NEW_TO_OLD (newest first) = descending order = DATE_ADDED_DESC
    // DATE_ADDED_OLD_TO_NEW (oldest first) = ascending order = DATE_ADDED_ASC
    if (sort === sortOptions.DATE_ADDED_NEW_TO_OLD) return 'DATE_ADDED_DESC'
    if (sort === sortOptions.DATE_ADDED_OLD_TO_NEW) return 'DATE_ADDED_ASC'
    if (sort === sortOptions.RELEVANCE) return 'RELEVANCE'
    // If it's already a sort option key, use it
    if (sort === 'DATE_ADDED_ASC' || sort === 'DATE_ADDED_DESC' || sort === 'RELEVANCE') {
      return sort as QuickSearchSortOption
    }
    return 'RELEVANCE'
  }, [sort])

  // Memoize includeTypes and includeFields to prevent unnecessary re-renders
  const includeTypes = useMemo(() => ['blog'] as ('blog' | 'product' | 'page')[], [])
  const includeFields = useMemo(
    () => [
      'title',
      'creators',
      'thumbnail_url',
      'product_url',
      'object_id',
      'categories',
      'date_added',
      'type',
    ],
    []
  )

  const { results, isLoading, totalCount } = useQuickSearch({
    query: query || '',
    sort: sortOption,
    pagination,
    includeTypes,
    includeFields,
  })

  // Calculate pagination info
  const totalPages = Math.ceil((totalCount || 0) / ITEMS_PER_PAGE)
  const hasPreviousPage = currentPage > 1
  const hasNextPage = currentPage < totalPages

  return (
    <>
      <BlogSearchResultsList
        results={results}
        isLoading={isLoading}
        searchTerm={query}
      />
      {query && totalCount > 0 && (
        <div className="pt-4">
          <Pagination
            page={currentPage}
            perPage={ITEMS_PER_PAGE}
            totalCount={totalCount}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            linkGenerator={page => {
              const params = new URLSearchParams(searchParams.toString())
              params.set('page', page.toString())
              if (query) params.set('query', query)
              if (sort) params.set('sort', sort)
              return `/blog/search?${params.toString()}`
            }}
          />
        </div>
      )}
    </>
  )
}

interface BlogSearchResultsClientProps {
  query: string
  sort: string | null
  currentPage: number
}

